<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { formatMoneyWithSymbol, formatPriceChange, priceChangeClass } from '@/lib/money'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { LineChart } from 'lucide-vue-next'
import PriceChangeCell from './PriceChangeCell.vue'
import SideHeading from './SideHeading.vue'
import type { MarketCurrencyBlock } from './catalogDetailTypes'

const { t } = useI18n()

/**
 * One entry per side the page shows, each loading on its own: the two markets are
 * fetched separately and one arriving late must not hold up the other.
 */
defineProps<{
    sides: { side: 'resell' | 'buyback'; blocks: MarketCurrencyBlock[]; loading: boolean }[]
}>()

</script>

<template>
    <Card>
        <CardHeader>
            <CardTitle class="flex items-center gap-2">
                <LineChart class="size-4" />
                {{ t('catalog.details.marketPrices') }}
            </CardTitle>
        </CardHeader>
        <CardContent>
            <!--
                One section per side under a single card, headed exactly as the suggested
                prices card heads its own sections, because the two are read together: a
                suggested price only means something against the market of its own side.
                Nothing is ever merged across a section — or across a currency inside one.
            -->
            <div class="space-y-8">
                <div v-for="entry in sides" :key="entry.side">
                    <SideHeading :side="entry.side" class="mb-3" />

                    <div v-if="entry.loading" class="space-y-3">
                        <Skeleton class="h-8 w-full" />
                        <Skeleton class="h-8 w-full" />
                        <Skeleton class="h-8 w-full" />
                    </div>

                    <p v-else-if="entry.blocks.length === 0" class="text-xs text-muted-foreground">
                        {{ t('catalog.details.marketPricesEmpty') }}
                    </p>

                    <!--
                        One block per currency, all of them on screen at once. The rollup
                        sits inside its block rather than above them: a median taken over
                        two currencies is a number denominated in nothing, so there is no
                        side-level figure to show and nothing is hidden behind a selector.
                    -->
                    <div v-else class="space-y-6">
                        <div v-for="block in entry.blocks" :key="block.currency">
                            <!--
                                The block carries no currency badge: every figure under it
                                is written with its own symbol, so naming the unit a second
                                time in the corner only competed with the rollup beside it.
                            -->
                            <div v-if="block.aggregate" class="mb-4">
                                <div class="flex flex-wrap items-baseline gap-6">
                                    <div>
                                        <p
                                            class="text-xs text-muted-foreground uppercase tracking-wide"
                                            :title="t('catalog.details.marketPricesTable.trendAvgHint')"
                                        >
                                            {{ t('catalog.details.marketPricesTable.trendAvg') }}
                                        </p>
                                        <p class="text-2xl font-bold tabular-nums">
                                            {{
                                                formatMoneyWithSymbol(
                                                    block.aggregate.trend_avg_price,
                                                    block.aggregate.currency_symbol,
                                                )
                                            }}
                                        </p>
                                    </div>
                                    <div>
                                        <p class="text-xs text-muted-foreground uppercase tracking-wide">
                                            {{ t('catalog.details.marketPricesTable.30d') }}
                                        </p>
                                        <p
                                            class="text-lg font-semibold tabular-nums"
                                            :class="priceChangeClass(block.aggregate.last_30_days_price_change)"
                                        >
                                            {{ formatPriceChange(block.aggregate.last_30_days_price_change) ?? '—' }}
                                        </p>
                                    </div>
                                    <div>
                                        <p class="text-xs text-muted-foreground uppercase tracking-wide">
                                            {{ t('catalog.details.marketPricesTable.60d') }}
                                        </p>
                                        <p
                                            class="text-lg font-semibold tabular-nums"
                                            :class="priceChangeClass(block.aggregate.last_60_days_price_change)"
                                        >
                                            {{ formatPriceChange(block.aggregate.last_60_days_price_change) ?? '—' }}
                                        </p>
                                    </div>
                                </div>
                            </div>

                            <!--
                                Fixed layout, so every table on the card lines up with the
                                one above it. There is one per currency per side, and with
                                auto layout each sizes its columns from its own rows — a
                                four-digit median in EUR then shifts the CHF grid beside it.
                            -->
                            <Table v-if="block.countries.length > 0" class="table-fixed">
                                <TableHeader>
                                    <TableRow>
                                        <TableHead class="w-[15%]">{{
                                            t('catalog.details.marketPricesTable.country')
                                        }}</TableHead>
                                        <TableHead class="text-right">{{
                                            t('catalog.details.marketPricesTable.min')
                                        }}</TableHead>
                                        <TableHead class="text-right">{{
                                            t('catalog.details.marketPricesTable.max')
                                        }}</TableHead>
                                        <TableHead
                                            class="text-right"
                                            :title="t('catalog.details.marketPricesTable.trendAvgHint')"
                                            >{{ t('catalog.details.marketPricesTable.trendAvg') }}</TableHead
                                        >
                                        <TableHead class="text-right">{{
                                            t('catalog.details.marketPricesTable.30d')
                                        }}</TableHead>
                                        <TableHead class="text-right">{{
                                            t('catalog.details.marketPricesTable.60d')
                                        }}</TableHead>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    <TableRow v-for="price in block.countries" :key="price.country">
                                        <TableCell class="font-medium uppercase">{{ price.country }}</TableCell>
                                        <TableCell class="text-right tabular-nums">
                                            {{ formatMoneyWithSymbol(price.min_price, price.currency_symbol) }}
                                        </TableCell>
                                        <TableCell class="text-right tabular-nums">
                                            {{ formatMoneyWithSymbol(price.max_price, price.currency_symbol) }}
                                        </TableCell>
                                        <TableCell class="text-right tabular-nums">
                                            {{ formatMoneyWithSymbol(price.trend_avg_price, price.currency_symbol) }}
                                        </TableCell>
                                        <TableCell class="text-right">
                                            <PriceChangeCell :value="price.last_30_days_price_change" />
                                        </TableCell>
                                        <TableCell class="text-right">
                                            <PriceChangeCell :value="price.last_60_days_price_change" />
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </div>
                    </div>
                </div>
            </div>
        </CardContent>
    </Card>
</template>
